import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { RightOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Collapse,
  DatePicker,
  Flex,
  InputNumber,
  message,
  Select,
  Space,
  Spin,
  Typography,
  theme,
} from 'antd'
import { AxiosResponse } from 'axios'

import {
  CarrierQuotaCarrierBlock,
  CarrierQuotaEquipmentRow,
  getCarrierQuotaConfigMatrix,
  putCarrierQuotaConfig,
  QUERY_KEYS,
} from '@api'

const { Title, Text } = Typography

/** Alinea cabecera, fila TTE y filas de equipo (misma grilla que la referencia de diseño). */
const GRID_TEMPLATE =
  'minmax(160px, 1.6fr) minmax(180px, 2fr) minmax(120px, 1fr) minmax(120px, 1fr)'

const recalcBlock = (
  block: CarrierQuotaCarrierBlock,
  workingDays: number,
): CarrierQuotaCarrierBlock => {
  const equipmentQuotas = block.equipmentQuotas.map((row: CarrierQuotaEquipmentRow) => ({
    ...row,
    monthlyQuota: row.dailyQuota * workingDays,
  }))
  const totalDailyQuota = equipmentQuotas.reduce(
    (s: number, r: CarrierQuotaEquipmentRow) => s + r.dailyQuota,
    0,
  )
  const totalMonthlyQuota = equipmentQuotas.reduce(
    (s: number, r: CarrierQuotaEquipmentRow) => s + r.monthlyQuota,
    0,
  )
  return { ...block, equipmentQuotas, totalDailyQuota, totalMonthlyQuota }
}

const recalcAll = (blocks: CarrierQuotaCarrierBlock[], workingDays: number) =>
  blocks.map(b => recalcBlock(b, workingDays))

const CarrierQuotaConfigView = () => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const queryClient = useQueryClient()
  const [month, setMonth] = useState<Dayjs>(() => dayjs().startOf('month'))
  const [workingDays, setWorkingDays] = useState(20)
  const [carriers, setCarriers] = useState<CarrierQuotaCarrierBlock[]>([])
  const [canEdit, setCanEdit] = useState(true)

  const year = month.year()
  const monthNum = month.month() + 1

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [QUERY_KEYS.CARRIER_QUOTA_CONFIG, year, monthNum],
    queryFn: () => getCarrierQuotaConfigMatrix(year, monthNum),
  })

  useEffect(() => {
    if (!data) return
    setWorkingDays(data.workingDays)
    setCanEdit(data.canEdit)
    setCarriers(recalcAll(data.carriers, data.workingDays))
  }, [data])

  const onWorkingDaysChange = (v: number | null) => {
    const wd = typeof v === 'number' && !Number.isNaN(v) ? v : 0
    setWorkingDays(wd)
    setCarriers(prev => recalcAll(prev, wd))
  }

  const setDailyQuota = useCallback(
    (carrierId: number, equipmentId: number, daily: number) => {
      setCarriers(prev =>
        prev.map(block => {
          if (block.carrierId !== carrierId) return block
          const equipmentQuotas = block.equipmentQuotas.map((row: CarrierQuotaEquipmentRow) =>
            row.equipmentId === equipmentId ? { ...row, dailyQuota: daily } : row,
          )
          return recalcBlock({ ...block, equipmentQuotas }, workingDays)
        }),
      )
    },
    [workingDays],
  )

  const saveMutation = useMutation({
    mutationFn: () =>
      putCarrierQuotaConfig({
        year,
        month: monthNum,
        workingDays,
        lines: carriers.flatMap(c =>
          c.equipmentQuotas.map((row: CarrierQuotaEquipmentRow) => ({
            carrierId: c.carrierId,
            equipmentId: row.equipmentId,
            dailyQuota: row.dailyQuota,
          })),
        ),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries([QUERY_KEYS.CARRIER_QUOTA_CONFIG, year, monthNum])
      message.success(t('admin_router.carrier_quotas.save_ok'))
    },
    onError: (e: unknown) => {
      const res = e as AxiosResponse<{ error?: { message?: string } }>
      const msg = res?.data?.error?.message
      message.error(msg || t('admin_router.carrier_quotas.save_error'))
    },
  })

  const dailyQuotaSelectOptions = useMemo(() => {
    const maxSeen = carriers.reduce(
      (acc, c) =>
        Math.max(
          acc,
          c.equipmentQuotas.reduce((m, r) => Math.max(m, r.dailyQuota), 0),
        ),
      0,
    )
    const upTo = Math.min(200, Math.max(40, maxSeen + 10))
    return Array.from({ length: upTo + 1 }, (_, i) => ({ value: i, label: String(i) }))
  }, [carriers])

  const collapseItems = useMemo(
    () =>
      carriers.map(c => ({
        key: String(c.carrierId),
        styles: {
          header: {
            background: token.colorPrimaryBg,
            alignItems: 'center',
          },
          body: { padding: 0 },
        },
        label: (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: GRID_TEMPLATE,
              alignItems: 'center',
              columnGap: 12,
              width: '100%',
              paddingRight: 8,
            }}
          >
            <Text strong>{c.tradeName}</Text>
            <Text type="secondary">—</Text>
            <Text strong style={{ textAlign: 'right' }}>
              {c.totalDailyQuota}
            </Text>
            <Text strong style={{ textAlign: 'right' }}>
              {c.totalMonthlyQuota}
            </Text>
          </div>
        ),
        children: (
          <div>
            {c.equipmentQuotas.map((row: CarrierQuotaEquipmentRow, idx: number) => (
              <div
                key={row.equipmentId}
                style={{
                  display: 'grid',
                  gridTemplateColumns: GRID_TEMPLATE,
                  alignItems: 'center',
                  columnGap: 12,
                  padding: '10px 16px 10px 24px',
                  borderTop: idx === 0 ? 'none' : `1px solid ${token.colorBorderSecondary}`,
                  background: token.colorBgContainer,
                }}
              >
                <span />
                <Text>{row.equipmentName}</Text>
                <div style={{ justifySelf: 'end', width: '100%', maxWidth: 100 }}>
                  <Select
                    size="small"
                    style={{ width: '100%' }}
                    value={row.dailyQuota}
                    disabled={!canEdit}
                    options={dailyQuotaSelectOptions}
                    onChange={v =>
                      setDailyQuota(c.carrierId, row.equipmentId, typeof v === 'number' ? v : 0)
                    }
                  />
                </div>
                <Text style={{ textAlign: 'right' }}>{row.dailyQuota * workingDays}</Text>
              </div>
            ))}
          </div>
        ),
      })),
    [canEdit, carriers, dailyQuotaSelectOptions, setDailyQuota, token, workingDays],
  )

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>{t('admin_router.carrier_quotas.title')}</Title>
      <Card>
        <Flex wrap="wrap" gap="large" align="flex-end" style={{ marginBottom: 20 }}>
          <Space direction="vertical" size={4}>
            <Text>{t('admin_router.carrier_quotas.select_month')}</Text>
            <DatePicker
              picker="month"
              value={month}
              onChange={d => {
                if (d) setMonth(d.startOf('month'))
              }}
              format="MMMM-YYYY"
              allowClear={false}
            />
          </Space>
          <Space direction="vertical" size={4}>
            <Text>{t('admin_router.carrier_quotas.working_days')}</Text>
            <InputNumber
              min={0}
              max={366}
              value={workingDays}
              disabled={!canEdit}
              onChange={onWorkingDaysChange}
            />
          </Space>
          <Button
            type="primary"
            loading={saveMutation.isLoading}
            disabled={!canEdit || isLoading}
            onClick={() => saveMutation.mutate()}
          >
            {t('admin_router.carrier_quotas.save')}
          </Button>
        </Flex>
        {!canEdit ? (
          <Text type="warning" style={{ display: 'block', marginBottom: 12 }}>
            {t('admin_router.carrier_quotas.readonly_closed')}
          </Text>
        ) : null}
        <Spin spinning={isLoading || isFetching}>
          {carriers.length === 0 && !isLoading ? (
            <Text type="secondary">{t('admin_router.carrier_quotas.empty')}</Text>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: GRID_TEMPLATE,
                  alignItems: 'center',
                  columnGap: 12,
                  padding: '8px 16px 8px 16px',
                  marginBottom: 4,
                  borderBottom: `1px solid ${token.colorBorder}`,
                  fontWeight: 600,
                }}
              >
                <Text type="secondary">{t('admin_router.carrier_quotas.col_carrier')}</Text>
                <Text type="secondary">{t('admin_router.carrier_quotas.col_equipment')}</Text>
                <Text type="secondary" style={{ textAlign: 'right' }}>
                  {t('admin_router.carrier_quotas.col_daily')}
                </Text>
                <Text type="secondary" style={{ textAlign: 'right' }}>
                  {t('admin_router.carrier_quotas.col_monthly')}
                </Text>
              </div>
              <Collapse
                bordered={false}
                expandIconPosition="end"
                expandIcon={({ isActive }) => (
                  <RightOutlined
                    rotate={isActive ? 90 : 0}
                    style={{
                      fontSize: 12,
                      color: token.colorTextSecondary,
                      transition: 'transform 0.2s',
                    }}
                  />
                )}
                items={collapseItems}
              />
            </>
          )}
        </Spin>
      </Card>
    </div>
  )
}

export { CarrierQuotaConfigView }
